import React, { FunctionComponent } from 'react';
import styled from 'styled-components';
import { DisplayMode } from '../desktop/MenuDesktopContent';
import { MenuLink } from './MenuLink';
import greenPatchLogo from '../../../../assets/img/green_patch.svg';
import { CONTENTBOX_PATCH } from '../../../common/constants';
import { showForUserMultiple } from '../../../utils/contentful';
import { useIsReseller, useIsRegisteredNonBuyer, useIsLoggedIn } from '../../../hooks/useLogin';
import { getMaxMenuDiscountPercentage, getPatchAndDiscountToDisplayInMenu } from '../../../utils/campaign';
import { PatchAndDiscount } from '../../../utils/contentBox';
import { SimplePatch } from '../../SimplePatch';
import { MenuItem } from '../MenuUtils';

interface Props {
  item: MenuItem;
  currentMenuPath: MenuItem[];
  menuSelectedPath: MenuItem[];
  setMenuSelectedPath: (menuItems: MenuItem[]) => void;
  withImages?: boolean;
  withIcons?: boolean;
  displayProductsAmount?: boolean;
  displayMode?: DisplayMode;
  setProductsDisplayMode?: (displayMode: DisplayMode) => void;
  hidePatch?: boolean;
  closeMenu: () => void;
  onClick: (label: string) => void;
}

export const MenuListItem: FunctionComponent<Props> = ({
  item,
  currentMenuPath,
  menuSelectedPath,
  setMenuSelectedPath,
  withImages,
  withIcons,
  displayMode,
  setProductsDisplayMode,
  hidePatch,
  closeMenu,
  onClick
}) => {
  const itemPath = [...currentMenuPath, item];
  const selected = itemPath.every((item, index) => item?.label === menuSelectedPath[index]?.label);
  const currentUserLoggedIn = useIsLoggedIn();
  const currentUserReseller = useIsReseller();
  const currentUserRegisteredNonBuyer = useIsRegisteredNonBuyer();

  const patchAndDiscountToDisplay = getPatchAndDiscountToDisplayInMenu(
    currentUserReseller,
    currentUserLoggedIn,
    currentUserRegisteredNonBuyer,
    item?.patchAndDiscount as PatchAndDiscount[]
  );
  const patchHasDiscountPercentage = item?.children && item?.patchAndDiscount?.map(patch => patch?.discountPercentage);

  const handleItemClick = (itemPath: MenuItem[]) => {
    const label = typeof itemPath[itemPath.length - 1].label !== 'undefined' ? itemPath[itemPath.length - 1].label : '';

    onClick(label);
    if (displayMode === DisplayMode.list) {
      setProductsDisplayMode && setProductsDisplayMode(DisplayMode.list);
    }
    item.children && !item.hideChildren && setMenuSelectedPath(itemPath);
  };

  return (
    <StyledMenuLink className="menu-link" item={item} onClick={() => handleItemClick(itemPath)} selected={selected} closeMenu={closeMenu}>
      {withImages && (
        <StyledImageContainer>
          <img src={item.img} alt="" />
        </StyledImageContainer>
      )}
      {withIcons && (
        <ImageContainer>
          <img className="icon" width="21" height="22" src={item.icon} alt="" />
        </ImageContainer>
      )}
      <StyledItem>
        <StyledLabel>
          {item.label}
          {patchAndDiscountToDisplay?.patch === CONTENTBOX_PATCH.ECOFRIENDLY && !item?.children && (
            <img style={{ maxHeight: '15px' }} src={greenPatchLogo} alt="" />
          )}
        </StyledLabel>
        {!hidePatch &&
          showForUserMultiple(
            patchAndDiscountToDisplay?.showCampaignOnlyForMultiple,
            currentUserReseller,
            currentUserLoggedIn,
            currentUserRegisteredNonBuyer
          ) && (
            <SimplePatch
              patch={patchAndDiscountToDisplay?.patch}
              discountPercentage={
                patchHasDiscountPercentage
                  ? getMaxMenuDiscountPercentage(item, currentUserReseller, currentUserLoggedIn, currentUserRegisteredNonBuyer)
                  : patchAndDiscountToDisplay?.discountPercentage
              }
            />
          )}
      </StyledItem>
    </StyledMenuLink>
  );
};

const StyledMenuLink = styled(MenuLink)<{ selected: boolean }>`
  cursor: pointer;
  font-weight: 300;
  text-decoration: none;
  color: ${props => (props.selected ? '#ff0b75' : '#323232')};
  transition: color 0.2s ease-in-out;
  background-color: ${props => (props.selected ? 'rgba(32,32,32,.03)' : 'white')};
  display: flex;
  align-items: center;
  user-select: none;
  padding: 2px;

  div span {
    font-weight: ${props => (props.selected ? '700' : '300')};
  }

  &:hover {
    color: #ff0b75;
  }

  img {
    height: 60px;

    &.icon {
      height: 22px;
      width: 22px;
    }
  }
`;

const StyledItem = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  .patch-container {
    position: relative;
    left: 0;
    top: 0;

    .patch {
      display: flex;
      padding: 2px 8px;
      justify-content: center;
      align-items: center;
      gap: 8px;
      font-family: 'StyreneA';
      font-weight: normal;
    }
  }
`;

const ImageContainer = styled.div`
  width: 25px;
  display: flex;
  justify-content: center;
  margin-right: 16px;
`;

const StyledLabel = styled.div`
  font-size: 14px;

  display: flex;
  flex-direction: row;
  line-height: 21px;
  > img {
    margin-top: 2px;
    margin-left: 4px;
    height: 15px;
    width: 15px;
    max-width: 15px;
  }
`;
const StyledImageContainer = styled.div`
  margin-right: 8px;
  img {
    width: 46px;
    height: 46px;
    border-radius: 46px;
    border: 1px solid var(--Black-100, #ebebeb);
    object-fit: cover;
  }
`;
